import { useCallback, useEffect, useState } from 'react'
import { useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js'
import axios, { AxiosError } from 'axios'
import { UNITED_STATES } from '@/constants/regions'
import { Offer } from '@/services/PifService'
import { PaymentMethod } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { OrderCompleteArgs } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'

type PaymentIntentType = {
  id?: string
  payment_method?: string | PaymentMethod | null
  status?: string
}

type PaymentUserDetails = {
  new_user: boolean
  token: string | null
  email: string | null
}

export const useAppleGooglePay = ({
  onComplete,
  offer,
  skip,
  onError,
  clientSecret,
  fetchUserDetails,
}: {
  isGuest?: boolean
  onComplete: (props: OrderCompleteArgs) => void
  onError?: (error: string) => void
  offer: Offer
  skip?: boolean
  clientSecret: string
  fetchUserDetails?: boolean
}) => {
  const stripe = useStripe()
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [errorMessageWithWallet, setErrorMessageWithWallet] = useState<string | null>(null)
  const [isSubmittingWithWallet, setIsSubmittingWithWallet] = useState<boolean>(false)
  const [isFetchingUserDetails, setIsFetchingUserDetails] = useState<boolean>(false)
  const track = useSafeTrack()

  const setError = useCallback(
    (error: string) => {
      setErrorMessageWithWallet(error)
      onError?.(error)
    },
    [onError],
  )

  const getPaymentUserDetails = useCallback(
    async (paymentIntentId?: string) => {
      if (!paymentIntentId || !fetchUserDetails) return { token: null, email: null }

      try {
        setIsFetchingUserDetails(true)
        const res = await pollForPaymentUserDetails(paymentIntentId)
        return { token: res.new_user ? res.token : null, email: res.email }
      } catch (err) {
        logger().error('Failed to retrieve payment user details', { paymentIntentId }, err)
        return { token: null, email: null }
      } finally {
        setIsFetchingUserDetails(false)
      }
    },
    [fetchUserDetails],
  )

  useEffect(() => {
    if (skip) return
    if (stripe && offer && !paymentRequest) {
      const pr: PaymentRequest = stripe.paymentRequest({
        country: UNITED_STATES,
        currency: offer.currency.toLowerCase(),
        total: {
          label: offer.name,
          amount: offer.price,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      // Check the availability of the Payment Request API.
      pr.canMakePayment()
        .then((result) => {
          if (result) {
            setPaymentRequest(pr)
          }
        })
        .catch((e) => {
          logger().error('Could not create wallet payment request', { offer, paymentRequest }, e)
          throw e
        })
    } else if (stripe && offer && paymentRequest) {
      paymentRequest.update({
        currency: offer.currency.toLowerCase(),
        total: {
          label: offer.name,
          amount: offer.price,
        },
      })
    }
  }, [stripe, offer, skip, paymentRequest])

  useEffect(() => {
    const canProceedWithPayment = stripe && paymentRequest && onComplete
    if (skip || !canProceedWithPayment) return

    const handlePaymentSuccess = async (paymentIntent: PaymentIntentType) => {
      const { token, email } = await getPaymentUserDetails(paymentIntent?.id)

      onComplete({
        orderId: paymentIntent?.id ?? 'unknown',
        paymentMethod: paymentIntent?.payment_method as string,
        email,
        token,
      })
      setIsSubmittingWithWallet(false)
      track('completed wallet paymentv2', { paymentMethod: paymentIntent?.payment_method })
    }

    const processPaymentIntent = async (ev: PaymentRequestPaymentMethodEvent) => {
      track('started wallet paymentv2')
      setIsSubmittingWithWallet(true)
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      )

      if (confirmError) {
        handlePaymentFailure(ev)
      } else if (paymentIntent) {
        handleConfirmedPayment(ev, paymentIntent)
      }
    }

    const handlePaymentFailure = (ev: PaymentRequestPaymentMethodEvent) => {
      setIsSubmittingWithWallet(false)
      logger().error('Wallet payment confirmation error', { paymentRequest })
      setError('Could not complete payment, please try again')
      ev.complete('fail')
    }

    const handleConfirmedPayment = async (ev: PaymentRequestPaymentMethodEvent, paymentIntent: PaymentIntentType) => {
      ev.complete('success')
      if (paymentIntent?.status === 'requires_action') {
        const { error } = await stripe.confirmCardPayment(clientSecret)
        if (error) {
          setIsSubmittingWithWallet(false)
          return // Exit early if there's an error
        }
      }
      handlePaymentSuccess(paymentIntent)
    }

    paymentRequest?.on('paymentmethod', async (ev) => {
      try {
        processPaymentIntent(ev)
      } catch (e) {
        logger().error('General error confirming wallet payment', { paymentRequest }, e)
        throw e
      }
    })
  }, [
    track,
    onComplete,
    clientSecret,
    stripe,
    paymentRequest,
    skip,
    setError,
    offer.id,
    offer.region,
    getPaymentUserDetails,
  ])

  return { isSubmittingWithWallet, paymentRequest, errorMessageWithWallet, isFetchingUserDetails }
}

const BIM_REST_ENDPOINT = process.env.NEXT_PUBLIC_BIM_REST_ENDPOINT || 'https://bim.angelstudios.com/api'

// BIM is notified by Stripe via webhook after Apple or Google Pay success. This function polls
// BIM for the user details matching a payment intent ID, and if the user is a guest, it returns
// a token they can be logged-in for 24 hours without having to set a password
export async function pollForPaymentUserDetails(paymentIntentId?: string): Promise<PaymentUserDetails> {
  if (!paymentIntentId) throw new Error('Payment intent ID is required')

  const maxAttempts = 10
  let attempts = 0

  return new Promise((resolve, reject) => {
    const attemptRequest = async () => {
      try {
        const res = await axios.get<PaymentUserDetails>(
          `${BIM_REST_ENDPOINT}/guest-token/?payment_intent_id=${paymentIntentId}`,
        )
        // eslint-disable-next-line no-console
        console.log('bim response', res)
        resolve(res.data)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('bim response error', error)
        if ((error as AxiosError)?.response?.status === 404) {
          attempts += 1
          if (attempts >= maxAttempts) {
            reject(new Error('Timeout polling for payment user details'))
          } else {
            setTimeout(attemptRequest, 2500)
          }
        } else {
          reject(error)
        }
      }
    }

    attemptRequest()
  })
}
